<template>
  <div class="report-quick-actions" :class="{ compact }">
    <v-btn
      v-for="button in visibleButtons"
      :key="button.action"
      :color="button.color"
      :small="compact"
      outlined
      @click.stop="run(button.action)"
    >
      {{ $t(compact ? `reports.actions.${button.action}` : button.title) }}
      <v-icon right :small="compact">{{ button.icon }}</v-icon>
    </v-btn>

    <template v-if="feature === 'medkitlog'">
      <medkitlogQrCodeDialog
        :medkitlogQrCodeDialog="qrDialog"
        @printPoster="openQrCodePoster"
      />
      <medkitlogReportExportForm :data="exportForm" />
    </template>

    <template v-if="feature === 'incidentReport'">
      <incidentReportQrCodeDialog
        :incidentReportQrCodeDialog="qrDialog"
        @printPoster="openQrCodePoster"
      />
      <incidentReportExportForm :data="exportForm" />
      <v-dialog v-model="typeChooserOpen" max-width="800px">
        <v-card>
          <v-list>
            <v-list-item>
              <v-btn block color="primary" @click="newDocument('incidentReportForm')">
                {{ $t("incidentReports.chooseType.nonReportable") }}
                {{ $t("incidentReports.chooseType.nonReportableSub") }}
              </v-btn>
            </v-list-item>
            <v-list-item>
              <v-btn block color="primary" @click="newDocument('incidentReportFormLong')">
                {{ $t("incidentReports.chooseType.reportable") }}
                {{ $t("incidentReports.chooseType.reportableSub") }}
              </v-btn>
            </v-list-item>
            <v-list-item>
              <v-btn block color="primary" @click="newDocument('incidentReportFormQuick')">
                {{ $t("incidentReports.chooseType.nearMiss") }}
              </v-btn>
            </v-list-item>
          </v-list>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import medkitlogQrCodeDialog from "../../views/medkitlog/medkitlogQrCodeDialog.vue";
import medkitlogReportExportForm from "../../views/medkitlog/medkitlogReportExportForm.vue";
import incidentReportQrCodeDialog from "../../views/incidentReport/incidentReportQrCodeDialog.vue";
import incidentReportExportForm from "../../views/incidentReport/incidentReportExportForm.vue";
import { downloadQrPoster } from "../../utils/posterDownload";
import { canCreate } from "../../utils/access";

const BUTTONS = {
  medkitlog: [
    { title: "medkitlogs.generateQrCode", color: "primary", action: "qr", icon: "qr_code", create: true },
    { title: "medkitlogs.createExport", color: "grey", action: "export", icon: "download" },
    { title: "medkitlogs.createMedkitlog", color: "secondary", action: "new", icon: "add", create: true },
  ],
  incidentReport: [
    { title: "medkitlogs.generateQrCode", color: "primary", action: "qr", icon: "qr_code", create: true },
    { title: "incidentReports.createExport", color: "grey", action: "export", icon: "download" },
    { title: "incidentReports.createIncidentReports", color: "secondary", action: "new", icon: "add", create: true },
  ],
  environmentalReport: [
    { title: "environmentalReports.create", color: "secondary", action: "new", icon: "add", create: true },
  ],
};

const FORMS = {
  medkitlog: "medkitlogForm",
  environmentalReport: "environmentalReportForm",
};

export default {
  name: "reportQuickActions",
  components: {
    medkitlogQrCodeDialog,
    medkitlogReportExportForm,
    incidentReportQrCodeDialog,
    incidentReportExportForm,
  },
  props: {
    feature: { type: String, required: true },
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      qrDialog: { open: false },
      exportForm: {
        open: false,
        title:
          this.feature === "incidentReport"
            ? "incidentReports.exportTitle"
            : "medkitlogs.exportTitle",
        item: null,
        method: "export",
      },
      typeChooserOpen: false,
    };
  },
  computed: {
    visibleButtons() {
      return (BUTTONS[this.feature] || []).filter(
        (button) => !button.create || canCreate(this.feature)
      );
    },
  },
  methods: {
    run(action) {
      if (action === "qr") this.qrDialog.open = true;
      if (action === "export") this.exportForm.open = true;
      if (action === "new") {
        if (this.feature === "incidentReport") this.typeChooserOpen = true;
        else this.newDocument(FORMS[this.feature]);
      }
    },
    newDocument(name) {
      this.typeChooserOpen = false;
      const { year, month } = this.$route.params;
      this.$router.push({
        name,
        params: { id: "1", mode: "new", data: 1 },
        query: year && month ? { year, month } : {},
      });
    },
    async openQrCodePoster() {
      try {
        const settings = await this.$store.dispatch(
          "getSettingData",
          "qrCodeKeys"
        );
        let incidentKey = settings.value.incidentReport;
        let medkitKey = settings.value.medkitlog;
        if (!incidentKey) {
          const t = await this.$store.dispatch("newIncidentReportQrCode");
          incidentKey = t.token;
        }
        if (!medkitKey) {
          const t = await this.$store.dispatch("newMedkitlogQrCode");
          medkitKey = t.token;
        }
        await downloadQrPoster(incidentKey, medkitKey);
      } catch (e) {
        console.error(e);
      }
    },
  },
};
</script>

<style scoped>
.report-quick-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 16px;
}
.report-quick-actions.compact {
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 0;
}
</style>
